import { cn } from '@/lib/cn'
import { MARK_NODE, MARK_PATH_D } from './BrandMark'

/**
 * The loading mark — the CrystalDEX monogram drawing itself.
 *
 * ── Why the logo and not a spinner ──
 * The first version of this component animated a row of bars rippling left to right. It was
 * rejected for the right reason: a row of bars is the reference product's idiom, and it stays
 * their idiom no matter what the bars are said to represent. A loader assembled from generic
 * parts — bars, arcs, dots — can only ever be someone else's loader with our colour on it.
 *
 * This one cannot be copied, because it is the mark. And the mark already tells the story the
 * wait is about: it is drawn as a single routed pipe, capital entering at the lower left,
 * crossing the diagonal and leaving through the brass node at the top right (see BrandMark.tsx).
 * Stroking it on progressively shows capital moving through that route. The figure is doing the
 * product's own work rather than filling time.
 *
 * ── What is animated, and the rule behind it ──
 * `stroke-dashoffset` on the pipe, `transform: scale` on the node. NO opacity, anywhere.
 *
 * theme.css:1056 records why, at length: Chrome freezes animation timelines in background tabs,
 * and a frozen timeline is pinned at whatever its 0% frame said — permanently. A 0% frame
 * containing `opacity: 0` is an element that is invisible forever. Here the worst case of a
 * frozen tab is a fully drawn monogram sitting still, which is a perfectly good thing to be
 * looking at.
 *
 * The unlit track underneath is what makes that true. The pipe is stroked over a faint copy of
 * the same path, so even at the instant the dash is fully retracted there is still an N on
 * screen — the mark never disappears, it only dims.
 */

/** One full pass: draw the route, then release it. */
export const ROUTED_PERIOD_MS = 1800

const SIZES = {
  sm: 'size-4',
  md: 'size-6',
  lg: 'size-9',
  xl: 'size-14',
} as const

export type MarkSize = keyof typeof SIZES

export function RoutedMark({
  size = 'md',
  className,
  label = 'Loading',
}: {
  size?: MarkSize
  className?: string
  label?: string
}) {
  return (
    <span
      // `status` announces once on appearance without stealing focus, which is what a transient
      // wait wants. The label carries the word, so the drawing itself stays hidden.
      role="status"
      aria-label={label}
      className={cn('inline-block', SIZES[size], className)}
    >
      <svg viewBox="0 0 64 64" fill="none" aria-hidden className="block size-full">
        {/*
         * The unlit route. Present at every frame, which is what stops a frozen timeline from
         * leaving an empty box — see the note above.
         */}
        <path
          d={MARK_PATH_D}
          className="stroke-accent/15"
          strokeWidth="11"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/*
         * `pathLength="100"` normalises the geometry so the keyframes can talk in percentages.
         * Without it the dash offsets would depend on the monogram's measured length, and
         * retuning the mark by a unit would silently retime the animation.
         */}
        <path
          d={MARK_PATH_D}
          pathLength="100"
          className="routed-pipe stroke-accent"
          strokeWidth="11"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ animationDuration: `${ROUTED_PERIOD_MS}ms` }}
        />
        {/* The exit terminal. Brass, because brass means reward everywhere else in the app. */}
        <circle
          cx={MARK_NODE.cx}
          cy={MARK_NODE.cy}
          r={MARK_NODE.r}
          className="routed-node fill-brass"
          style={{ animationDuration: `${ROUTED_PERIOD_MS}ms` }}
        />
      </svg>
    </span>
  )
}

/**
 * The mark centred in a block, for a region waiting on data or on a route chunk.
 *
 * `minHeight` rather than a fixed height: this stands in for content of unknown size, and a
 * fixed box would guarantee a jump in one direction or the other when the real thing lands.
 */
export function PendingPanel({
  label,
  className,
  size = 'xl',
}: {
  label?: string
  className?: string
  size?: MarkSize
}) {
  return (
    <div className={cn('flex min-h-[240px] flex-col items-center justify-center gap-3', className)}>
      <RoutedMark size={size} label={label ?? 'Loading'} />
      {label && <p className="text-sm text-muted">{label}</p>}
    </div>
  )
}

/**
 * A card-shaped placeholder with the mark centred in it.
 *
 * ── Why this exists alongside Skeleton rather than replacing it ──
 * `Skeleton` is right for LINE-shaped things — a pill, a table row, a figure — where the grey
 * box is itself a fair picture of what is coming, and the reader can read the shape of the page
 * before the data lands. It is not retired and should not be.
 *
 * What it does badly is a large AREA. A 340px slab of pulsing grey predicts nothing useful
 * about the chart or the card stack that will replace it; it just makes a third of the viewport
 * flat and grey. This variant keeps the one thing the slab was doing that mattered — reserving
 * the exact box, so nothing shifts on arrival — and spends the interior on the mark instead.
 *
 * Caller supplies the size, and it must be the size of the real content. The layout-shift rule
 * in ui/primitives.tsx applies here unchanged.
 */
export function PendingSurface({ className, label }: { className?: string; label?: string }) {
  return (
    <div className={cn('grid place-items-center rounded-lg bg-surface ring-1 ring-line', className)}>
      <RoutedMark size="xl" label={label ?? 'Loading'} />
    </div>
  )
}

/**
 * Inline variant for a panel that already has a frame and a heading — the body only.
 * Shorter than `PendingPanel` because the surrounding card is supplying the presence.
 */
export function PendingInline({ className, label }: { className?: string; label?: string }) {
  return (
    <div className={cn('flex min-h-[120px] items-center justify-center', className)}>
      <RoutedMark size="lg" label={label ?? 'Loading'} />
    </div>
  )
}
